import React from "react";

import { distribuicaoDeRecompensas } from "./recompensas";

/**
 * O percurso inteiro em miniatura, na pílula do cabeçalho.
 *
 * POR QUE EXISTE. A trilha só dizia a POSIÇÃO ("Fase 3 de 22"), e posição não é
 * progresso: quem entra na fase 3 de 22 não sabe se está no começo ou no meio sem
 * fazer a conta. O progresso propriamente dito existia num `aria-live` invisível
 * (`X de Y fases concluídas`), ou seja só para quem usa leitor de tela.
 *
 * E o tabuleiro não responde essa pergunta sozinho. Medido em `ava_homologa` em
 * 21/08/2026: a mediana é de 10 fases, o máximo é 39, e **42% das trilhas passam de
 * 10**. Numa de 22 o aluno vê 4 nós de cada vez — 18% do percurso — e rola 2,6
 * telas por leva. Ele nunca vê o conjunto.
 *
 * BARRA CONTÍNUA, E NÃO UM PONTO POR FASE. Um ponto por fase é mais informativo e
 * não escala: em 39 fases num celular de 375px cada ponto teria 8px de espaço
 * (contando o gap), e a fileira viraria uma linha pontilhada ilegível justamente
 * na trilha longa, que é onde a barra serve. A barra vale de 2 a N sem mudar de
 * comportamento.
 *
 * O QUE ELA MARCA, além do progresso: o FIM DE CADA LEVA. É o marco que dá textura
 * ao percurso ("falta pouco para o grande momento") sem virar ruído.
 *
 * O NOME DA MARCA MUDOU DE SIGNIFICADO em 24/08/2026, e vale registrar porque a
 * classe CSS continua `__percurso-carta`. Ela era documentada como "onde cai a carta
 * rara", e a carta rara passou a ser SORTEADA — 2 em 3 vezes não cai. O que a marca
 * sempre APONTOU, de fato, é o fim de leva: `distribuicaoDeRecompensas` é geometria
 * da trilha e não sabe de sorteio nenhum. Então o desenho não muda; o que muda é o
 * que se pode afirmar sobre ele.
 *
 * E isso é o certo aqui, e não um sorteio a mais para a barra: o fim de leva
 * SEMPRE entrega a cena do arco — o mascote contando a sua parte da história —, e é
 * disso que a barra avisa. Marcar só as raras sorteadas seria, além de tudo, contar
 * ao aluno o resultado de um sorteio antes de ele chegar lá.
 *
 * As cartas COMUNS ficaram fora, e por medida: numa trilha de 39 fases elas são 17,
 * e a barra tem 62px no desktop e 40px no celular. Em 39 fases as marcas se
 * empilhavam com vão NEGATIVO (-4px), ou seja uma por cima da outra — a fileira
 * virava um tracejado ilegível, medido em `tc-barra-percurso.js`. Os fins de leva
 * são 1 ou 2 por trilha e caberiam em qualquer tamanho. Quem anuncia a carta comum
 * é o baú no próprio nó, de perto, que é onde a informação serve para decidir.
 *
 * NÃO É NAVEGÁVEL, de propósito. Quem navega é o tabuleiro (setas, arrasto, scroll,
 * encaixe — quatro camadas já). Uma quinta, num alvo de 8px de altura, seria a
 * menos acessível de todas e a mais fácil de tocar por acidente.
 *
 * @param {number} totalFases
 * @param {number} concluidas quantas fases o servidor diz que estão feitas
 * @param {number} indiceAtual índice 0-based da fase atual; -1 quando terminou
 */
function BarraDePercurso({ totalFases, concluidas, indiceAtual }) {
  if (!totalFases || totalFases <= 0) return null;

  const { raras } = distribuicaoDeRecompensas(totalFases);
  // O CENTRO do segmento da fase, e não a borda: a barra divide a largura em
  // `totalFases` fatias iguais, e a fase 1 ocupa a primeira. Marcar a borda
  // deixaria a marca da fase 1 encostada na ponta esquerda e a da última fora da
  // barra.
  const centroDaFase = numero => ((numero - 0.5) / totalFases) * 100;
  const feito = (concluidas / totalFases) * 100;
  const terminou = indiceAtual === -1;

  return (
    <div
      className="trilha-ludica__percurso"
      // `progressbar` e não `meter`: o suporte de `meter` em leitor de tela é
      // irregular, e o que interessa aqui é "quanto do caminho já foi".
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={totalFases}
      aria-valuenow={concluidas}
      aria-valuetext={`${concluidas} de ${totalFases} fases concluídas`}
    >
      <span
        className="trilha-ludica__percurso-feito"
        style={{ width: `${feito}%` }}
      />

      {/* O fim de cada leva. Uma ou duas por trilha, e é sempre onde a cerimônia
          do arco acontece — com carta rara, se o sorteio der. */}
      {raras.map(numero => (
        <span
          key={numero}
          className="trilha-ludica__percurso-carta"
          style={{ left: `${centroDaFase(numero)}%` }}
        />
      ))}

      {/* "Você está aqui". Some quando a trilha termina, pelo mesmo motivo que o
          explorador some do tabuleiro: sem próxima fase, a marcação perde a
          função — e a barra cheia já diz tudo. */}
      {!terminou && (
        <span
          className="trilha-ludica__percurso-agora"
          style={{ left: `${centroDaFase(indiceAtual + 1)}%` }}
        />
      )}
    </div>
  );
}

export default BarraDePercurso;
